import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { Lock, Download, RefreshCw, Loader2, Eye, Users, Mail, TrendingUp, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ icon: Icon, label, value, suffix, testid }) => (
  <div
    className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl"
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-white/45">
      <Icon className="h-4 w-4" />
      <span className="text-xs uppercase tracking-[0.2em]">{label}</span>
    </div>
    <p className="mt-3 font-display text-4xl font-thin text-white">
      {value}
      {suffix && <span className="text-2xl text-white/50">{suffix}</span>}
    </p>
  </div>
);

export default function LeadsDashboard() {
  const [key, setKey] = useState(localStorage.getItem("radiate_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [syncMsg, setSyncMsg] = useState("");
  const [syncing, setSyncing] = useState(false);

  const syncFlodesk = async () => {
    setSyncing(true);
    setSyncMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/flodesk/backfill`, null, {
        params: { key },
      });
      setSyncMsg(`Synced ${data.succeeded}/${data.processed} to Flodesk` + (data.failed ? ` · ${data.failed} failed` : ""));
    } catch (err) {
      setSyncMsg("Flodesk sync failed. Check the API key.");
    } finally {
      setSyncing(false);
    }
  };

  const load = useCallback(async (k) => {
    setStatus("loading");
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/analytics`, { params: { key: k } });
      setData(data);
      setAuthed(true);
      localStorage.setItem("radiate_admin_key", k);
      setStatus("idle");
    } catch (err) {
      setStatus("error");
      setAuthed(false);
      setError(err?.response?.status === 401 ? "Wrong access key." : "Could not load dashboard.");
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = () => {
    const leads = data?.leads || [];
    const rows = [["email", "source", "created_at"]];
    leads.forEach((l) => rows.push([l.email, l.source || "", l.created_at || ""]));
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `radiate-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (!authed) {
    return (
      <div className="grain flex min-h-screen items-center justify-center bg-[#0a0a0c] px-6">
        <div className="w-full max-w-sm rounded-[2rem] border border-white/10 bg-white/[0.03] p-8 backdrop-blur-2xl">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full border border-white/15 bg-white/5">
            <Lock className="h-5 w-5 text-white/70" />
          </div>
          <h1 className="mt-6 text-center text-2xl font-thin tracking-tight text-white">
            Radiate dashboard
          </h1>
          <p className="mt-2 text-center text-sm text-white/50">
            Enter your access key to view analytics.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              load(key);
            }}
            className="mt-6 flex flex-col gap-3"
          >
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Access key"
              className="w-full rounded-full border border-white/15 bg-white/5 px-5 py-3.5 text-sm text-white placeholder:text-white/35 outline-none focus:border-white/40"
              data-testid="admin-key-input"
            />
            {status === "error" && (
              <p className="text-xs text-[#ffb7b2]" data-testid="admin-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-medium text-black transition-transform duration-300 hover:scale-105 disabled:opacity-70"
              data-testid="admin-unlock"
            >
              {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const t = data?.totals || {};
  const leads = data?.leads || [];
  const recent = data?.recent_views || [];

  return (
    <div className="grain min-h-screen bg-[#0a0a0c] px-6 py-16 md:px-10">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/40">Radiate</p>
            <h1 className="mt-2 font-display text-4xl font-thin tracking-tight text-white">Dashboard</h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => load(key)}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm text-white transition-colors hover:bg-white/10"
              data-testid="leads-refresh"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              onClick={syncFlodesk}
              disabled={syncing}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm text-white transition-colors hover:bg-white/10 disabled:opacity-60"
              data-testid="flodesk-sync"
            >
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Sync to Flodesk
            </button>
            <button
              onClick={exportCsv}
              disabled={!leads.length}
              className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-medium text-black transition-transform duration-300 hover:scale-105 disabled:opacity-50"
              data-testid="leads-export"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
        </div>

        {syncMsg && (
          <p className="mt-4 text-sm text-[#ffb7b2]" data-testid="flodesk-sync-msg">
            {syncMsg}
          </p>
        )}

        {/* stat cards */}
        <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat icon={Eye} label="Page views" value={t.views ?? 0} testid="stat-views" />
          <Stat icon={Users} label="Unique visitors" value={t.unique_visitors ?? 0} testid="stat-visitors" />
          <Stat icon={Mail} label="Email submissions" value={t.leads ?? 0} testid="stat-leads" />
          <Stat icon={TrendingUp} label="Conversion" value={t.conversion ?? 0} suffix="%" testid="stat-conversion" />
        </div>

        {/* chart */}
        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl">
          <p className="mb-6 text-xs uppercase tracking-[0.2em] text-white/45">Views · last 30 days</p>
          <div className="h-64 w-full" data-testid="views-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.views_by_day || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="vfill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a855f7" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#a855f7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 11 }}
                  tickFormatter={(d) => d.slice(5)}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={24}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                />
                <Tooltip
                  contentStyle={{
                    background: "#14121c",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 12,
                    color: "#fff",
                  }}
                  labelStyle={{ color: "rgba(255,255,255,0.6)" }}
                />
                <Area type="monotone" dataKey="views" stroke="#c084fc" strokeWidth={2} fill="url(#vfill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* leads + recent views */}
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="overflow-hidden rounded-2xl border border-white/10">
            <p className="border-b border-white/10 bg-white/[0.04] px-6 py-4 text-xs uppercase tracking-[0.2em] text-white/45">
              Email submissions ({leads.length})
            </p>
            <div className="max-h-[420px] overflow-y-auto" data-testid="leads-table">
              {leads.length === 0 ? (
                <p className="px-6 py-10 text-center text-sm text-white/40">No leads yet.</p>
              ) : (
                leads.map((l, i) => (
                  <div key={i} className="border-t border-white/5 px-6 py-4 first:border-t-0" data-testid="lead-row">
                    <p className="text-sm text-white/85">{l.email}</p>
                    <p className="mt-1 text-xs text-white/40">
                      {l.source || "—"} ·{" "}
                      {l.created_at ? new Date(l.created_at).toLocaleString() : "—"}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-white/10">
            <p className="border-b border-white/10 bg-white/[0.04] px-6 py-4 text-xs uppercase tracking-[0.2em] text-white/45">
              Recent individual views
            </p>
            <div className="max-h-[420px] overflow-y-auto" data-testid="views-table">
              {recent.length === 0 ? (
                <p className="px-6 py-10 text-center text-sm text-white/40">No views yet.</p>
              ) : (
                recent.map((v, i) => (
                  <div key={i} className="border-t border-white/5 px-6 py-4 first:border-t-0">
                    <p className="text-sm text-white/80">
                      {v.referrer && v.referrer !== "direct" ? v.referrer : "Direct visit"}
                    </p>
                    <p className="mt-1 text-xs text-white/40">
                      {v.visitor_id?.slice(0, 8)} · {v.ts ? new Date(v.ts).toLocaleString() : "—"}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
